import type { FeatureFlagVariant } from './feature-flag';

export const FLAG_OVERRIDE_COOKIE = 'blinq-flag-overrides';

export const flagOverridesEnabled = (): boolean =>
  process.env.NEXT_PUBLIC_DEPLOYMENT_DOMAIN !== 'production';

export type FlagOverrides = Partial<Record<keyof FeatureFlagVariant, string>>;

export function parseFlagOverrides(raw: string | null | undefined): FlagOverrides {
  if (!raw) {
    return {};
  }
  try {
    const json = atob(raw.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as FlagOverrides) : {};
  } catch {
    return {};
  }
}

/**
 * Local file-based flag overrides, read from the git-ignored
 * `flag-overrides.local.json` at the webapp root. The file is read once at
 * dev-server startup in `next.config.mjs` and injected as a plain-JSON string
 * env var, so this accessor is isomorphic (works on both client and server)
 * without any filesystem access. Returns `{}` when disabled, unset, empty, or
 * malformed — never throws.
 */
export function getLocalFlagOverrides(): FlagOverrides {
  if (!flagOverridesEnabled()) {
    return {};
  }
  const raw = process.env.NEXT_PUBLIC_LOCAL_FLAG_OVERRIDES;
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as FlagOverrides) : {};
  } catch {
    return {};
  }
}

/**
 * Resolved flag overrides combining the local file and the override cookie.
 * Cookie overrides win over local file overrides for the same flag.
 */
export function getEffectiveOverrides(cookieRaw: string | null | undefined): FlagOverrides {
  return { ...getLocalFlagOverrides(), ...parseFlagOverrides(cookieRaw) };
}
